/**
 * Event capture + management, relocated out of the (removed) left sidebar into the main flow.
 * The structured form captures the high-signal fields the engine depends on (date precision,
 * an exact clock time if known, location); adding or deleting an event is an evidence change,
 * so it resets `loop_count` and re-scores — which re-lights the heatmap live.
 */

import { useState } from "react";
import { useProfile } from "../state/ProfileContext";
import { EVENT_CATEGORIES } from "../lib/config";
import { DATE_PRECISION_DAYS, DEFAULT_DATE_PRECISION, build_structured_event } from "../lib/event_intake";
import { process_answers_into_profile } from "../lib/rectification";
import { carry_ai_cost, get_openai_api_key } from "../lib/openai_service";
import { rescore_candidates_in_place } from "../lib/scoring";
import { Banner, useAsyncAction } from "../ui/common";
import { shortTime, titleCase } from "../ui/format";
import { formatISODate, todayLocal } from "../lib/datetime";

export function AddEventForm() {
  const { user_profile, mutate, reload } = useProfile();
  const addAction = useAsyncAction();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState(
    EVENT_CATEGORIES.includes("marriage") ? "marriage" : EVENT_CATEGORIES[0],
  );
  const [evDate, setEvDate] = useState("");
  const [precision, setPrecision] = useState(DEFAULT_DATE_PRECISION);
  const [knowTime, setKnowTime] = useState(false);
  const [evTime, setEvTime] = useState("");
  const [placeName, setPlaceName] = useState("");
  const [polarity, setPolarity] = useState("positive");
  const [confidence, setConfidence] = useState("1");

  const birthDate: string | undefined = user_profile?.birth?.date;

  function resetForm() {
    setLabel("");
    setEvDate("");
    setPrecision(DEFAULT_DATE_PRECISION);
    setKnowTime(false);
    setEvTime("");
    setPlaceName("");
    setPolarity("positive");
    setConfidence("1");
  }

  async function submit() {
    await addAction.run("Saving event…", async () => {
      const event = await build_structured_event({
        start_date: evDate,
        category,
        label,
        date_precision: precision,
        event_time: knowTime && evTime ? `${evTime}:00` : null,
        place_name: placeName || null,
        polarity,
        confidence: Number(confidence),
        birth_date: birthDate,
      });
      mutate((p) => {
        (p.events ??= []).push(event);
        p.loop_count = 0;
        rescore_candidates_in_place(p);
      });
      reload();
      resetForm();
    });
  }

  return (
    <div>
      <button className="ghost" onClick={() => setOpen((o) => !o)}>
        {open ? "▾" : "▸"} ➕ Add a dated event
      </button>
      {open && (
        <div style={{ marginTop: "0.5rem" }}>
          {addAction.error && <Banner kind="error">{addAction.error}</Banner>}
          <label className="field">
            <span>What happened?</span>
            <input value={label} maxLength={180} onChange={(e) => setLabel(e.target.value)} />
          </label>
          <div className="row">
            <label className="field">
              <span>Category</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {EVENT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {titleCase(c)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Date it happened</span>
              <input
                type="date"
                value={evDate}
                max={formatISODate(todayLocal())}
                onChange={(e) => setEvDate(e.target.value)}
              />
            </label>
          </div>
          <div className="row">
            <label className="field">
              <span>How precise is the date?</span>
              <select value={precision} onChange={(e) => setPrecision(e.target.value)}>
                {Object.keys(DATE_PRECISION_DAYS).map((k) => (
                  <option key={k} value={k}>
                    {titleCase(k)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Where did it happen? (optional)</span>
              <input
                value={placeName}
                maxLength={120}
                placeholder="e.g. Delhi, India"
                onChange={(e) => setPlaceName(e.target.value)}
              />
            </label>
          </div>
          <label className="inline-tag" style={{ marginBottom: "0.5rem" }}>
            <input
              type="checkbox"
              checked={knowTime}
              style={{ width: "auto" }}
              onChange={(e) => setKnowTime(e.target.checked)}
            />
            I know the clock time it happened
          </label>
          {knowTime && (
            <label className="field">
              <span>Time of the event</span>
              <input type="time" value={evTime} onChange={(e) => setEvTime(e.target.value)} />
            </label>
          )}
          <div className="row">
            <label className="field">
              <span>Positive or difficult?</span>
              <select value={polarity} onChange={(e) => setPolarity(e.target.value)}>
                <option value="positive">positive</option>
                <option value="negative">negative</option>
                <option value="neutral">neutral</option>
              </select>
            </label>
            <label className="field">
              <span>Sure of the date? ({confidence})</span>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={confidence}
                onChange={(e) => setConfidence(e.target.value)}
              />
            </label>
          </div>
          <button className="primary full" disabled={addAction.pending || !evDate} onClick={submit}>
            {addAction.pending ? "Saving…" : "Add event"}
          </button>
        </div>
      )}
    </div>
  );
}

export function SavedEvents() {
  const { user_profile, mutate } = useProfile();
  const events: any[] = user_profile?.events ?? [];
  return (
    <div className="sidebar-section">
      <h3>Saved events ({events.length})</h3>
      {events.length === 0 && <p className="caption">No events yet — add one above.</p>}
      {events.map((event, idx) => {
        const evLabel = event.label || event.category || "Event";
        return (
          <div className="event-item" key={idx}>
            <div>
              <strong>{evLabel}</strong>
              <div className="caption">
                {event.start_date}
                {event.event_time ? ` ${shortTime(event.event_time)}` : ""} · {event.category}
              </div>
            </div>
            <button
              className="danger"
              title={`Delete “${evLabel}”`}
              onClick={() =>
                mutate((p) => {
                  (p.events ??= []).splice(idx, 1);
                  p.loop_count = 0;
                  rescore_candidates_in_place(p);
                })
              }
            >
              ✕
            </button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * AI event intake: free text → dated events. Also EDITS existing events — the model receives
 * current_events and marks a correction with replaces_label, which replaces the old event
 * wholesale (process_answers_into_profile). Any add/edit resets loop_count and re-lights the
 * heatmap.
 */
export function AiEventBox() {
  const { user_profile, run_data, mutate, reload } = useProfile();
  const [freeText, setFreeText] = useState("");
  const aiAction = useAsyncAction();
  const hasKey = !!get_openai_api_key();
  if (!user_profile || !run_data || !hasKey) return null;

  async function runAi() {
    await aiAction.run("Asking OpenAI to extract / update dated events…", async () => {
      // process_answers_into_profile mutates the objects; clone, run, persist via mutate.
      const profile = structuredClone(user_profile!);
      const run = structuredClone(run_data!);
      await process_answers_into_profile(profile, run, [], {}, freeText);
      mutate((p, r) => {
        p.events = profile.events;
        p.loop_count = 0;
        rescore_candidates_in_place(p);
        r.log = run.log;
        carry_ai_cost(r, run);
      });
      setFreeText("");
      reload();
    });
  }

  return (
    <div className="ai-event-box">
      <h4>🤖 Describe events in your own words</h4>
      <p className="caption">
        Add new events <em>or correct ones already saved</em> — e.g. "I got married in June 2014
        in Delhi" or "actually the wedding was March 2015, not 2014". Corrections replace the
        saved event and the whole chart recalculates.
      </p>
      <textarea
        rows={4}
        value={freeText}
        placeholder="e.g. I got married in June 2014 in Delhi. My first child was born in March 2017…"
        onChange={(e) => setFreeText(e.target.value)}
      />
      {aiAction.error && <Banner kind="error">{aiAction.error}</Banner>}
      <button
        className="primary"
        style={{ marginTop: "0.5rem" }}
        disabled={aiAction.pending || !freeText.trim()}
        onClick={runAi}
      >
        {aiAction.pending ? "Extracting…" : "Add / update events with AI"}
      </button>
    </div>
  );
}

/** The Add-Events panel body: AI add/edit + the structured form. The saved-events list lives on
 *  the main page's "Saved information" table, so it's intentionally not repeated here. */
export function EventsPanelBody() {
  return (
    <>
      <AiEventBox />
      <AddEventForm />
    </>
  );
}
